<template>
  <div>
    <v-card outlined>
      <div class='route-container route rounded grey lighten-4'>
        <div style="height: 5px;" width='100%' class='blue-grey lighten-4 rounded-top'></div>

        <div class="route-header-row">  <!-- Top row -->
              
          <v-tooltip open-delay='700' top>
            <template v-slot:activator='{ on }'>
              <v-btn x-small icon class='mr-auto' v-on='on' :disabled='disableMapping' @click='mapGroup'>
                <v-icon small color='grey darken-1'>mdi-map-marker</v-icon>
              </v-btn>
            </template>
            <span>Map {{ filterLawnCare ? "(displayed projects only)" : "" }}</span>
          </v-tooltip>
            
          <span class='route-header pilot primary--text'>{{ group.route_group }}</span>

          <v-tooltip open-delay='700' top>
            <template v-slot:activator='{ on }'>
              <v-btn x-small class='ml-auto' icon v-on='on' @click='editGroup'>
                <v-icon small>mdi-pencil-outline</v-icon>
              </v-btn>
            </template>
            <span>Edit route details</span>
          </v-tooltip>

        </div>

        <div class="route-header-row">  <!-- Middle row -->
           
          <span class='my-n1 caption route-header info--text'>{{ group.description }}</span>
          
        </div>

        <div class="route-header-row">  <!-- Bottom row -->

          <div class='mr-auto button-holder'></div>

          <span class='caption route-header vehicle grey--text' v-if='isLawnDivision'>
            {{ lawnCareProjects.length + ' Lawn Care / ' + group.projects.length + ' Total' }}
          </span>
          <span class='caption route-header vehicle grey--text' v-else>
            {{ group.projects.length + ' Projects' }}
          </span>
          <span v-if='isSnowDivision' class='caption route-header primary--text font-weight-bold ml-1'>
            {{ "(" + ratingTotals + ")" }}
          </span>

          <v-tooltip open-delay='500' top>
            <template v-slot:activator='{ on }'>
              <span v-if='group.unrouted.length' v-on='on' class='ml-2 caption route-header vehicle error--text font-weight-bold'>
                {{ group.unrouted.length + '!Num'}}
              </span>
            </template>
            <span class='caption'>
              You have {{group.unrouted.length}} project{{group.unrouted.length > 1 ? 's' : ''}} not ordered within the group
            </span><br><span class='caption'> (route group is defined, but no number set)</span>
            <br><span class='caption'>These will not be included if group is mapped</span>
            <br><span class='caption'>(and are not included in the totals to the left)</span>
          </v-tooltip>

          <v-tooltip open-delay='500' top>
            <template v-slot:activator='{ on }'>
              <span v-if='noGeo' v-on='on' class='ml-2 caption route-header vehicle green--text font-weight-bold'>
                {{ noGeo + '!Geo'}}
              </span>
            </template>
            <span class='caption'>You have {{ noGeo }} project{{ noGeo > 1 ? 's' : ''}} missing geolocation data</span>
          </v-tooltip>

          <div class='ml-auto button-holder'>
            <v-tooltip open-delay='700' top>
              <template v-slot:activator='{ on }'>
                <v-btn x-small icon v-on='on' @click='toggleRouteItems'>
                  <v-icon small>{{showRouteItems ? 'mdi-arrow-collapse-up' : 'mdi-arrow-expand-down'}}</v-icon>
                </v-btn>
               </template>
              <span>{{ showRouteItems ? "Hide Group" : "Show Group"}}</span>
            </v-tooltip>
          </div>

        </div>
        <div :class='showRouteItems ? "scrollable" : ""'>
          <project-list
            v-if='showRouteItems && !!group.unrouted.length'
            :items='filteredUnrouted'
            :areProjectsSelected='areProjectsSelected'
            :isLoading='isLoading'
            @clicked='clicked'
            @insertBefore='insertBefore'
            @update='update'
            @openCrewVisits='openCrewVisits'
            @openQuickView='openQuickView'
            @getLatLng='getLatLng'
            hideScroll
          ></project-list>
          <project-list
            v-if='showRouteItems'
            :items='filteredProjects'
            :areProjectsSelected='areProjectsSelected'
            :isLoading='isLoading'
            @clicked='clicked'
            @insertBefore='insertBefore'
            @update='update'
            @openCrewVisits='openCrewVisits'
            @openQuickView='openQuickView'
            @getLatLng='getLatLng'
            hideScroll
          ></project-list>
          <v-btn
            v-if='areProjectsSelected && showRouteItems'
            small
            block
            tile
            depressed
            :disabled='isLoading'
            @click="addToRoute"
          >
            Add to end
          </v-btn>
        </div>
        <div style="height: 5px;" width='100%' class='blue-grey lighten-4 rounded-bottom'></div>
      </div>
    </v-card>

  </div>
</template>

<script>
import ProjectList from './ProjectList';
import _ from 'lodash';

export default {
  name: 'route-group',
  props: {
    group: {
      required: true
    },
    isLoading: {
      required: true
    },
    areProjectsSelected: {
      required: true
    },
    filterLawnCare: {
      required: false,
      type: Boolean,
      default: false,
    },
    showAllProjects: {
      required: false,
      type: Boolean,
      default: false,
    }
  },
  data: () => ({
    showEditRoute: false,
    showRouteItems: false,
    isMapping: false,
    routeCopy: {},
  }),
  watch: {
    showAllProjects() {
      this.showRouteItems = this.showAllProjects
    }
  },
  computed: {
    disableMapping () {
      if (!this.showRouteItems || this.isMapping) {
        return true
      } 
      return false
    },
    noGeo () {
      let tmp = _.filter(this.group.projects, p => !p.lat || !p.lng)
      let also = _.filter(this.group.unrouted, p => !p.lat || !p.lng)
      return tmp.length + also.length
    },
    lawnCareProjects () {
      return _.filter(this.group.projects, p => p.has_lawn_care)
    },
    isLawnDivision () {
      return this.$route.params.id === _.find(this.$store.state.misc.divisions, d => d.division === 'Lawn').id
    },
    isSnowDivision () {
      let snowDiv = _.find(this.$store.state.misc.divisions, d => d.division === 'Snow')
      return this.$route.params.id == snowDiv.id
    },
    ratingTotals() {
      if (!this.isSnowDivision) {
        return ""
      } else {
        return _.sumBy(this.group.projects, 'project_detail.driveway_rating_obj.rating').toFixed(1)
      }
    },
    filteredProjects () {
      if (this.filterLawnCare) {
        return _.filter(this.group.projects, p => p.has_lawn_care)
      }
      return this.group.projects
    },
    filteredUnrouted () {
      if (this.filterLawnCare) {
        return _.filter(this.group.unrouted, p => p.has_lawn_care)
      }
      return this.group.unrouted
    }
  },
  methods: {
    mapGroup () {
      this.isMapping = true
      this.$emit('mapGroup', this.group)
      setTimeout(() => {
        this.isMapping = false
      }, this.group.projects.length * 50)
    },
    toggleRouteItems () {
      this.showRouteItems = !this.showRouteItems
    },
    editGroup () {
      this.$emit('editGroup', _.cloneDeep(this.group))
    },
    update (group) {
      this.$emit('updateRoute', group)
      this.showEditRoute = false
    },
    clicked (proj) {
      if (proj.is_selected === true) {
        this.$emit('deselect', proj)
      } else {
        this.$emit('select', proj)
      }
    },
    addToRoute () {
      this.$emit('move', this.group.id)
    },
    insertBefore (proj) {
      this.$emit('move', this.group.id, proj.pk)
    },
    openCrewVisits (proj) {
      this.$emit('openCrewVisits', proj)
    },
    openQuickView (proj) {
      this.$emit('openQuickView', proj)
    },
    getLatLng (proj) {
      this.$emit('getLatLng', proj)
    }
  },
  components: {
    'project-list': ProjectList,
  }
}
</script>

<style scoped>

.route {
  border: 0px solid rgba(0,0,0,0.3);
}

.rounded {
  border-radius: 5px;
}

.rounded-top {
  border-top-left-radius: 3px;
  border-top-right-radius: 3px;
}

.route-header {
  text-align: center;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.route-header-row {
  display: flex;
  justify-content: center;
}

.route-title {
  font-size: 1rem;
  line-height: 1rem;
  text-transform: uppercase;
}

.route-details {
  font-size: 0.8rem;
  line-height: 0.8rem;
}

.route-container {
  width: 220px !important;
  /*overflow-y: auto;*/
  /*overflow-x: hidden;*/
  /*margin-bottom: 2px;*/
}

.pilot {
  font-size: 13px;
  font-weight: 600;
}

.button-holder {
  height: 20px;
  width: 20px;
  display:flex;
  align-items:center;
  justify-content:center;
}

.number-holder {
  height: 20px;
  width: 60px;
  align-items:center;
}

.scrollable {
  height: 540px;
  overflow-y: auto;
  overflow-x: hidden;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.scrollable::-webkit-scrollbar {
  display: none
}

</style>